import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface InlineCellEditorProps {
  field: {
    id: string;
    name: string;
    type: string;
    config?: any;
  };
  value: any;
  isEditing: boolean;
  isSelected: boolean;
  onStartEdit: () => void;
  onSave: (value: any) => void;
  onCancel: () => void;
  onKeyDown?: (e: KeyboardEvent) => void;
}

export const InlineCellEditor = ({
  field,
  value,
  isEditing,
  isSelected,
  onStartEdit,
  onSave,
  onCancel,
  onKeyDown,
}: InlineCellEditorProps) => {
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    onSave(editValue);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && field.type !== "long_text") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditValue(value);
      onCancel();
    } else if (e.key === "Tab") {
      e.preventDefault();
      handleSave();
      onKeyDown?.(e);
    }
  };

  // Render display value when not editing
  const renderDisplayValue = () => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-muted-foreground">—</span>;
    }

    switch (field.type) {
      case "checkbox":
        return value ? (
          <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">✓</Badge>
        ) : <span className="text-muted-foreground">—</span>;
        
      case "date":
        return format(new Date(value), "MMM d, yyyy");
        
      case "single_select":
        return (
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            {value}
          </Badge>
        );
        
      case "multi_select":
        const values = Array.isArray(value) ? value : [value];
        return (
          <div className="flex flex-wrap gap-1">
            {values.slice(0, 2).map((v: string, i: number) => (
              <Badge key={i} variant="secondary" className="bg-primary/10 text-primary text-xs">
                {v}
              </Badge>
            ))}
            {values.length > 2 && (
              <Badge variant="secondary" className="text-xs">+{values.length - 2}</Badge>
            )}
          </div>
        );
        
      case "url":
        return (
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-primary hover:underline truncate block max-w-[200px]"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        );
        
      case "email":
        return (
          <a 
            href={`mailto:${value}`} 
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        );
        
      case "currency":
        return `$${Number(value).toFixed(2)}`;
        
      case "percent":
        return `${Number(value)}%`;
        
      case "formula":
        return <span className="text-muted-foreground italic">{value}</span>;
        
      default:
        return <span className="truncate block max-w-[200px]">{String(value)}</span>;
    }
  };

  // Render editor when editing
  const renderEditor = () => {
    switch (field.type) {
      case "long_text":
        return (
          <Textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="min-h-[60px] text-sm border-0 focus-visible:ring-1 p-1"
            autoFocus
          />
        );

      case "number":
      case "currency":
      case "percent":
        return (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="number"
            value={editValue ?? ""}
            onChange={(e) => setEditValue(e.target.value ? Number(e.target.value) : null)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="h-full text-sm border-0 focus-visible:ring-1 p-1"
            autoFocus
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center justify-center h-full">
            <Checkbox
              checked={!!editValue}
              onCheckedChange={(checked) => {
                setEditValue(checked);
                onSave(checked);
              }}
            />
          </div>
        );

      case "date":
        return (
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-left font-normal h-full p-1",
                  !editValue && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {editValue ? format(new Date(editValue), "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={editValue ? new Date(editValue) : undefined}
                onSelect={(date) => {
                  const dateStr = date ? date.toISOString().split("T")[0] : null;
                  setEditValue(dateStr);
                  onSave(dateStr);
                  setDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );

      case "single_select":
        const options = field.config?.options || [];
        return (
          <div className="flex flex-wrap gap-1 p-1">
            {options.map((opt: string) => (
              <Badge
                key={opt}
                variant={editValue === opt ? "default" : "outline"}
                className="cursor-pointer text-xs"
                onClick={() => {
                  setEditValue(opt);
                  onSave(opt);
                }}
              >
                {opt}
              </Badge>
            ))}
            {editValue && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0"
                onClick={() => {
                  setEditValue(null);
                  onSave(null);
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        );

      case "multi_select":
        const multiOptions = field.config?.options || [];
        const selectedValues = Array.isArray(editValue) ? editValue : editValue ? [editValue] : [];
        return (
          <div className="flex flex-wrap gap-1 p-1">
            {multiOptions.map((opt: string) => (
              <Badge
                key={opt}
                variant={selectedValues.includes(opt) ? "default" : "outline"}
                className="cursor-pointer text-xs"
                onClick={() => {
                  const newValues = selectedValues.includes(opt)
                    ? selectedValues.filter((v: string) => v !== opt)
                    : [...selectedValues, opt];
                  setEditValue(newValues);
                  onSave(newValues);
                }}
              >
                {opt}
              </Badge>
            ))}
          </div>
        );

      case "email":
        return (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="email"
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="h-full text-sm border-0 focus-visible:ring-1 p-1"
            autoFocus
          />
        );

      case "url":
        return (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="url"
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="h-full text-sm border-0 focus-visible:ring-1 p-1"
            placeholder="https://"
            autoFocus
          />
        );

      case "phone":
        return (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="tel"
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="h-full text-sm border-0 focus-visible:ring-1 p-1"
            autoFocus
          />
        );

      case "formula":
        // Formula fields are read-only
        return <span className="text-muted-foreground italic p-1">{value || "—"}</span>;

      default:
        return (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="h-full text-sm border-0 focus-visible:ring-1 p-1"
            autoFocus
          />
        );
    }
  };

  return (
    <div
      data-grid-cell
      className={cn(
        "min-h-[36px] flex items-center",
        isSelected && !isEditing && "ring-2 ring-primary ring-inset",
        isEditing && "ring-2 ring-primary bg-background",
        !isEditing && "cursor-pointer"
      )}
      onClick={() => !isEditing && onStartEdit()}
      onDoubleClick={() => !isEditing && onStartEdit()}
    >
      {isEditing ? renderEditor() : renderDisplayValue()}
    </div>
  );
};
